import CardGiftcardOutlinedIcon from "@mui/icons-material/CardGiftcardOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    Link,
    MenuItem,
    Select,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import Logger from "../../../services/Logger";
import {
    PUZZLE_COINS_APP,
    resolveJigsawMuseCoins,
    resolveSlidingQuestCoins,
    resolveTotalPuzzleCoins,
    resolveWebHintPuzzleCoins,
    resolveWordWheelQuestCoins,
} from "../../../services/PuzzleCoinsUtil";
import UtilService from "../../../services/UtilService";
import CustomSnackbar from "../../components/CustomSnackbar";
import PuzzleCoinsService from "../coins/PuzzleCoinsService";

const PUZZLE_TYPE_LABELS = {
    SLIDING: "Sliding",
    JIGSAW: "Jigsaw",
    WORD_WHEEL: "Word Wheel",
    HINT: "Web hint",
};

const PUZZLE_TYPE_TO_APP = {
    SLIDING: PUZZLE_COINS_APP.SLIDING_PUZZLE_QUEST,
    JIGSAW: PUZZLE_COINS_APP.JIGSAW_MUSE,
    WORD_WHEEL: PUZZLE_COINS_APP.WORD_WHEEL_QUEST,
    HINT: PUZZLE_COINS_APP.HINT,
};

const PUZZLE_COINS_APP_LABELS = {
    [PUZZLE_COINS_APP.HINT]: "Web hint",
    [PUZZLE_COINS_APP.WEB_HINT]: "Web hint",
    [PUZZLE_COINS_APP.SLIDING_PUZZLE_QUEST]: "Sliding Puzzle Quest",
    [PUZZLE_COINS_APP.JIGSAW_MUSE]: "Jigsaw Muse",
    [PUZZLE_COINS_APP.WORD_WHEEL_QUEST]: "Word Wheel Quest",
};

const PUZZLE_COINS_APP_ORDER = [
    PUZZLE_COINS_APP.WEB_HINT,
    PUZZLE_COINS_APP.SLIDING_PUZZLE_QUEST,
    PUZZLE_COINS_APP.JIGSAW_MUSE,
    PUZZLE_COINS_APP.WORD_WHEEL_QUEST,
];

function formatEpochMs(value) {
    if (value == null || value === 0) return "—";
    return UtilService.formatMDY(value) || "—";
}

function formatCoins(value) {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return "—";
    }
    return Number(value).toLocaleString();
}

function apiFailureMessage(result, fallback = "Request failed") {
    if (result && typeof result === "object" && result.code === "FAILURE") {
        return result.message || fallback;
    }
    return null;
}

function puzzleTypeChipColor(type) {
    if (type === "SLIDING") return "primary";
    if (type === "JIGSAW") return "secondary";
    if (type === "WORD_WHEEL") return "success";
    if (type === "PURCHASE") return "warning";
    if (type === "HINT") return "error";
    return "default";
}

function puzzleTypeLabel(type) {
    if (!type) return "—";
    return PUZZLE_TYPE_LABELS[type] ?? type;
}

function resolveCoinsByApp(puzzleCoins) {
    const webHint = resolveWebHintPuzzleCoins({ puzzleCoins });
    return {
        [PUZZLE_COINS_APP.WEB_HINT]: webHint,
        [PUZZLE_COINS_APP.HINT]: webHint,
        [PUZZLE_COINS_APP.SLIDING_PUZZLE_QUEST]: resolveSlidingQuestCoins({ puzzleCoins }),
        [PUZZLE_COINS_APP.JIGSAW_MUSE]: resolveJigsawMuseCoins({ puzzleCoins }),
        [PUZZLE_COINS_APP.WORD_WHEEL_QUEST]: resolveWordWheelQuestCoins({ puzzleCoins }),
        [PUZZLE_COINS_APP.TOTAL]: resolveTotalPuzzleCoins({ puzzleCoins }),
    };
}

function resolveCoinsForPuzzleType(puzzleType, coinsByApp) {
    const appCode = PUZZLE_TYPE_TO_APP[puzzleType];
    if (!appCode || !coinsByApp) {
        return 0;
    }
    const n = Number(coinsByApp[appCode]);
    return Number.isFinite(n) ? n : 0;
}

/**
 * Per-user puzzle completion coins for admin subscription page.
 */
export default function PuzzleCoinsAdmin({ cloudUserId, puzzleCoins = null, onPuzzleCoinsChange = null }) {
    const [loading, setLoading] = useState(false);
    const [reloadToken, setReloadToken] = useState(0);
    const [totalCoins, setTotalCoins] = useState(null);
    const [coinsByApp, setCoinsByApp] = useState(null);

    const [pageModel, setPageModel] = useState({ page: 0, pageSize: 25 });
    const [sort, setSort] = useState([{ field: "earnedAt", sort: "desc" }]);
    const [rows, setRows] = useState([]);
    const [rowCount, setRowCount] = useState(0);

    const [filterPuzzleType, setFilterPuzzleType] = useState("");
    const [snackbar, setSnackbar] = useState({ open: false, severity: "success", message: "" });

    const [grantOpen, setGrantOpen] = useState(false);
    const [grantTargetCoins, setGrantTargetCoins] = useState("");
    const [grantSubmitting, setGrantSubmitting] = useState(false);

    const subjectUserId = typeof cloudUserId === "string" ? cloudUserId.trim() : "";

    const bumpReload = useCallback(() => setReloadToken((t) => t + 1), []);

    const applyCoinsByApp = useCallback(
        (nextCoinsByApp) => {
            if (!nextCoinsByApp || typeof nextCoinsByApp !== "object") {
                return;
            }
            const byApp = resolveCoinsByApp(nextCoinsByApp);
            setCoinsByApp(byApp);
            setTotalCoins(byApp[PUZZLE_COINS_APP.TOTAL]);
            if (typeof onPuzzleCoinsChange === "function") {
                onPuzzleCoinsChange(nextCoinsByApp);
            }
        },
        [onPuzzleCoinsChange],
    );

    const showSnack = useCallback((severity, message) => {
        setSnackbar({ open: true, severity, message });
    }, []);

    useEffect(() => {
        if (puzzleCoins && typeof puzzleCoins === "object") {
            const byApp = resolveCoinsByApp(puzzleCoins);
            setCoinsByApp(byApp);
            setTotalCoins(byApp[PUZZLE_COINS_APP.TOTAL]);
        }
    }, [puzzleCoins]);

    useEffect(() => {
        setPageModel((prev) => ({ ...prev, page: 0 }));
    }, [filterPuzzleType, subjectUserId]);

    useEffect(() => {
        if (!subjectUserId) {
            setTotalCoins(null);
            setCoinsByApp(null);
            return;
        }

        if (puzzleCoins && typeof puzzleCoins === "object") {
            return;
        }

        let cancelled = false;

        (async () => {
            try {
                const result = await PuzzleCoinsService.getUserTotal(subjectUserId);
                if (cancelled) return;
                const err = apiFailureMessage(result);
                if (err) {
                    setTotalCoins(null);
                    setCoinsByApp(null);
                    return;
                }
                setTotalCoins(result?.totalCoins ?? 0);
            } catch (e) {
                Logger.error("PuzzleCoinsAdmin total:", e);
                if (!cancelled) {
                    setTotalCoins(null);
                    setCoinsByApp(null);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [subjectUserId, puzzleCoins]);

    useEffect(() => {
        if (!subjectUserId) {
            setRows([]);
            setRowCount(0);
            return;
        }

        let cancelled = false;

        async function load() {
            setLoading(true);
            try {
                const result = await PuzzleCoinsService.listCoins(pageModel, sort, {
                    userId: subjectUserId,
                    puzzleType: filterPuzzleType.trim(),
                });
                if (cancelled) return;
                const err = apiFailureMessage(result);
                if (err) {
                    showSnack("error", err);
                    setRows([]);
                    setRowCount(0);
                    return;
                }
                setRows(
                    (result?.content || []).map((row) => ({
                        ...row,
                        id: row.id,
                    })),
                );
                setRowCount(PuzzleCoinsService.totalElements(result));
            } catch (e) {
                Logger.error("PuzzleCoinsAdmin list:", e);
                if (!cancelled) {
                    setRows([]);
                    setRowCount(0);
                    showSnack("error", "Failed to load puzzle coins");
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        load();
        return () => {
            cancelled = true;
        };
    }, [pageModel, sort, filterPuzzleType, subjectUserId, showSnack, reloadToken]);

    const openGrantCoins = () => {
        const puzzleType = filterPuzzleType.trim();
        if (!puzzleType) {
            return;
        }
        const current = resolveCoinsForPuzzleType(puzzleType, coinsByApp);
        setGrantTargetCoins(String(current));
        setGrantOpen(true);
    };

    const handleGrantCoins = async () => {
        const puzzleType = filterPuzzleType.trim();
        const targetCoins = Number(grantTargetCoins);
        if (!subjectUserId || !puzzleType || !Number.isFinite(targetCoins) || targetCoins < 0) {
            showSnack("warning", "Puzzle type and a non-negative coin total are required");
            return;
        }

        const currentCoins = resolveCoinsForPuzzleType(puzzleType, coinsByApp);
        const delta = targetCoins - currentCoins;
        if (delta === 0) {
            showSnack("info", "Coins are already at that amount");
            return;
        }

        setGrantSubmitting(true);
        try {
            const result = await PuzzleCoinsService.grantCoins({
                userId: subjectUserId,
                puzzleType,
                targetCoins,
            });
            const err = apiFailureMessage(result);
            if (err) {
                showSnack("error", err);
                return;
            }
            if (result?.coinsByApp) {
                applyCoinsByApp(result.coinsByApp);
            }
            const granted = Number(result?.coinsGranted) || 0;
            showSnack(
                "success",
                granted > 0
                    ? `Granted ${formatCoins(granted)} coin${granted === 1 ? "" : "s"} for ${PUZZLE_TYPE_LABELS[puzzleType] ?? puzzleType}`
                    : "No change applied",
            );
            setGrantOpen(false);
            setGrantTargetCoins("");
            bumpReload();
        } catch (e) {
            Logger.error("PuzzleCoinsAdmin grant coins:", e);
            showSnack("error", "Failed to grant coins");
        } finally {
            setGrantSubmitting(false);
        }
    };

    const columns = useMemo(
        () => [
            {
                field: "puzzleType",
                headerName: "Type",
                width: 110,
                renderCell: (params) => (
                    <Chip
                        label={puzzleTypeLabel(params.value)}
                        size="small"
                        color={puzzleTypeChipColor(params.value)}
                        variant="outlined"
                    />
                ),
            },
            { field: "puzzleId", headerName: "Puzzle ID", flex: 1, minWidth: 180 },
            { field: "playId", headerName: "Play ID", flex: 1, minWidth: 180 },
            { field: "coins", headerName: "Coins", width: 90, type: "number" },
            {
                field: "earnedAt",
                headerName: "Earned",
                width: 130,
                valueFormatter: (value) => formatEpochMs(value),
            },
        ],
        [],
    );

    if (!subjectUserId) {
        return null;
    }

    return (
        <Card sx={{ mt: 3 }}>
            <CardContent>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: 1,
                        flexWrap: "wrap",
                        mb: 1,
                    }}
                >
                    <Typography variant="h5" component="h2" sx={{ m: 0 }}>
                        Puzzle Coins
                    </Typography>
                    <Link
                        component={RouterLink}
                        to="/admin/account/coins"
                        variant="body2"
                        sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
                    >
                        All puzzle coins
                        <OpenInNewIcon sx={{ fontSize: 16 }} />
                    </Link>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    First-completion coins for sliding, jigsaw, and word wheel. Web hint spends/grants
                    use type <code>HINT</code> (<code>/v1/puzzle-coins</code>).
                </Typography>

                <Grid container spacing={1.5} sx={{ mb: 2 }}>
                    <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                        <Box
                            sx={{
                                p: 1.5,
                                borderRadius: 1,
                                border: "1px solid",
                                borderColor: "divider",
                                bgcolor: "action.hover",
                                height: "100%",
                            }}
                        >
                            <Typography variant="caption" color="text.secondary" display="block">
                                Total coins
                            </Typography>
                            <Typography variant="h6" component="p" sx={{ mt: 0.5, fontWeight: 700, m: 0 }}>
                                {formatCoins(totalCoins)}
                            </Typography>
                        </Box>
                    </Grid>
                    {PUZZLE_COINS_APP_ORDER.map((appCode) => (
                        <Grid key={appCode} size={{ xs: 12, sm: 6, md: 2.4 }}>
                            <Box
                                sx={{
                                    p: 1.5,
                                    borderRadius: 1,
                                    border: "1px solid",
                                    borderColor: "divider",
                                    bgcolor: "action.hover",
                                    height: "100%",
                                }}
                            >
                                <Typography variant="caption" color="text.secondary" display="block">
                                    {PUZZLE_COINS_APP_LABELS[appCode] ?? appCode}
                                </Typography>
                                <Typography variant="h6" component="p" sx={{ mt: 0.5, fontWeight: 700, m: 0 }}>
                                    {formatCoins(coinsByApp?.[appCode] ?? 0)}
                                </Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>

                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2, alignItems: "center" }}>
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel id="user-puzzle-coins-type-filter-label">Puzzle type</InputLabel>
                        <Select
                            labelId="user-puzzle-coins-type-filter-label"
                            label="Puzzle type"
                            value={filterPuzzleType}
                            onChange={(e) => setFilterPuzzleType(e.target.value)}
                        >
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="HINT">Web hint</MenuItem>
                            <MenuItem value="SLIDING">Sliding</MenuItem>
                            <MenuItem value="JIGSAW">Jigsaw</MenuItem>
                            <MenuItem value="WORD_WHEEL">Word Wheel</MenuItem>
                        </Select>
                    </FormControl>
                    <Tooltip title="Set per-type puzzle coins total (testing)">
                        <span>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<CardGiftcardOutlinedIcon />}
                                disabled={!filterPuzzleType.trim()}
                                onClick={openGrantCoins}
                            >
                                Grant coins
                            </Button>
                        </span>
                    </Tooltip>
                </Box>

                <DataGrid
                    rows={rows}
                    columns={columns}
                    rowCount={rowCount}
                    paginationModel={pageModel}
                    onPaginationModelChange={setPageModel}
                    pageSizeOptions={[25, 50, 100]}
                    paginationMode="server"
                    sortingMode="server"
                    sortModel={sort}
                    onSortModelChange={setSort}
                    loading={loading}
                    disableRowSelectionOnClick
                    autoHeight
                    hideFooterSelectedRowCount
                />
            </CardContent>

            <Dialog
                open={grantOpen}
                onClose={() => {
                    if (!grantSubmitting) setGrantOpen(false);
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Grant puzzle coins</DialogTitle>
                <DialogContent
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        pt: 3,
                        overflow: "visible",
                    }}
                >
                    <Alert severity="warning">Testing purpose only.</Alert>
                    <Alert severity="info">
                        Sets the lifetime puzzle coins total for the selected type by inserting an admin
                        adjustment row (<code>user_puzzle_coins</code>).
                    </Alert>
                    <TextField label="User ID" value={subjectUserId} fullWidth disabled />
                    <TextField
                        label="Puzzle type"
                        value={PUZZLE_TYPE_LABELS[filterPuzzleType.trim()] ?? filterPuzzleType.trim()}
                        fullWidth
                        disabled
                    />
                    <TextField
                        label="Coins total"
                        type="number"
                        value={grantTargetCoins}
                        onChange={(e) => setGrantTargetCoins(e.target.value)}
                        helperText={`Current total: ${formatCoins(
                            resolveCoinsForPuzzleType(filterPuzzleType.trim(), coinsByApp),
                        )}`}
                        fullWidth
                        autoFocus
                        slotProps={{ htmlInput: { min: 0 } }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setGrantOpen(false)} disabled={grantSubmitting}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={handleGrantCoins} disabled={grantSubmitting}>
                        {grantSubmitting ? "Applying…" : "Apply coins"}
                    </Button>
                </DialogActions>
            </Dialog>

            <CustomSnackbar
                open={snackbar.open}
                setOpen={(open) => setSnackbar((s) => ({ ...s, open }))}
                message={snackbar.message}
                severity={snackbar.severity}
            />
        </Card>
    );
}
